import { type Modification, type ModificationGroupLimits } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Determines the first modification group with unmet minimum aggregate quantity.
 */
export function findFirstUnmetGroupLimits(
  activeModifications: Modification[],
  groupLimits: ModificationGroupLimits,
) {
  const activeModificationsByCount = activeModifications.reduce(groupActiveModificationsByCount, {}); // prettier-ignore
  const findMinUnmetGroup = findMinUnmetGroupFactory(activeModificationsByCount, groupLimits); // prettier-ignore
  const firstMinUnmetGroup = Object.keys(groupLimits).find(findMinUnmetGroup);

  if (!firstMinUnmetGroup) {
    return;
  }

  return groupLimits[firstMinUnmetGroup];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function groupActiveModificationsByCount(
  counts: ModificationsGroupedByCount,
  modification: Modification,
) {
  const { modificationGroupName, quantity } = modification;

  const currentCount = counts[modificationGroupName] ?? 0;
  const updatedCount = currentCount + quantity;

  return { ...counts, [modificationGroupName]: updatedCount };
}

function findMinUnmetGroupFactory(
  activeModificationsByCount: ModificationsGroupedByCount,
  groupLimits: ModificationGroupLimits,
) {
  return function (groupName: string) {
    const selectedGroupLimits = groupLimits[groupName];
    const currentCount = activeModificationsByCount[groupName] ?? 0;

    return currentCount < selectedGroupLimits.minAggregateQuantity;
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ModificationsGroupedByCount = Record<string, number>;
